import styled from '@emotion/styled';
import { Icon } from '@mdi/react';
import { BaseButton, Tooltip, Variables } from '../../base';
import { SidebarItem } from '../sidebar-item';

const { ThemeLight } = Variables;

/** The groups of an app's nav: 14px between groups, 16px from the top. */
export const SidebarNav = styled.nav`
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding-top: 16px;
`;

export const NavGroup = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 12px;
`;

export const NavItems = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;

    a {
        display: block;
        text-decoration: none;
    }
`;

const groupLabelStyles = `
    display: flex;
    align-items: center;
    height: 24px;
    padding: 0 10px;
    font-size: 12px;
    line-height: 16px;
    font-weight: 600;
    color: ${ThemeLight.TextMild};
`;

export const NavGroupLabel = styled.div`
    ${groupLabelStyles}
`;

export const NavGroupToggle = styled(BaseButton)`
    ${groupLabelStyles}
    justify-content: space-between;
    width: 100%;
    border: none;
    border-radius: ${Variables.Rounded.medium};
    background: transparent;
    cursor: pointer;

    svg {
        fill: currentColor;
    }

    &:hover,
    &:focus-visible {
        color: ${ThemeLight.TextStrong};
    }
`;

/** In the rail a group's label becomes a rule between groups. */
export const NavGroupDivider = styled.div`
    height: 1px;
    margin: 4px 8px 8px;
    background-color: ${ThemeLight.Border1};
`;

export const NavItem = styled(SidebarItem)<{ rail: boolean }>`
    height: 34px;
    min-height: 34px;
    margin: 0;
    padding: ${({ rail }) => (rail ? 0 : '0 10px')};
    gap: 10px;
    justify-content: ${({ rail }) => (rail ? 'center' : 'flex-start')};
    border-left: none;
    border-radius: ${Variables.Rounded.medium};
    color: ${ThemeLight.TextStrong};
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    transition: background-color 120ms ease-out;

    &:not(.active):hover,
    &:not(.active):active,
    &:not(.active):focus {
        color: ${ThemeLight.TextStrong};
        background-color: ${ThemeLight.NeutralMild1};
    }

    /* Accent text on a tinted surface takes the dark end of its ramp (NS-2): the design's
       PrimaryConstant6 gives 3.8:1 here, under the 4.5:1 that 14px text needs. */
    &.active {
        color: ${ThemeLight.PrimaryInverted3};
        background-color: ${ThemeLight.PrimaryOpacity2};
        box-shadow: inset 0 0 0 1px ${ThemeLight.PrimaryOpacity3};
        border-left: none;
        font-weight: 600;
    }
`;

export const NavIcon = styled(Icon)`
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    fill: currentColor;
    opacity: 0.85;
`;

export const NavLabel = styled.span`
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
`;

/** Block-level Tooltip so a whole rail item is the hover target, not just its icon. */
export const RailTooltip = styled(Tooltip)`
    display: block;
`;

/** Below the groups: a rule, then the footer items (Settings). */
export const SidebarNavFooter = styled.div`
    margin-top: 16px;
    padding: 10px 12px 0;
    border-top: 1px solid ${ThemeLight.Border1};
`;
